import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "./axiosInstance";

export default function usePagedRegister(
  endpoint,
  {
    search = "",
    extraParams = {},
    pageSize = 50,
    autoOpenFirst = false,
  } = {}
) {
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const timer = useRef(null);

  const fetchPage = useCallback(async ({ append = false } = {}) => {
    if (loading) return [];
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        append && next ? next : endpoint,
        append && next
          ? undefined
          : {
              params: {
                page_size: pageSize,
                ...(search.trim() ? { search: search.trim() } : {}),
                ...extraParams,
              },
            }
      );
      const payload = response.data || {};
      const rows = Array.isArray(payload)
        ? payload
        : payload.results || [];
      setItems((old) => append ? [...old, ...rows] : rows);
      setNext(Array.isArray(payload) ? null : payload.next || null);
      setCount(Array.isArray(payload) ? rows.length : payload.count || 0);
      return rows;
    } finally {
      setLoading(false);
    }
  }, [endpoint, extraParams, loading, next, pageSize, search]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetchPage({ append: false }).catch(() => {});
    }, search.trim() ? 220 : 0);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [search, JSON.stringify(extraParams)]); // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = (event) => {
    const node = event.currentTarget;
    if (
      next &&
      !loading &&
      node.scrollTop + node.clientHeight >= node.scrollHeight - 120
    ) {
      fetchPage({ append: true }).catch(() => {});
    }
  };

  return { items, setItems, next, loading, count, reload: fetchPage, onScroll };
}
